/**
 * Affix moudle implementation
 *
 * @see http://www.w3schools.com/bootstrap/bootstrap_ref_js_asp
 *
 * The Affix plugin allows an element to become affixed (locked) to an area on the page.
 * This is often used with navigation menus or social icon buttons, to make them "stick"
 * at a specific area while scrolling up and down the page.
 *
 * The plugin toggles this behavior on and off (changes the value of CSS position from
 * static to fixed), depending on scroll position.
 *
 * @example JS usage
 * var jQuery = require('lib/jQuery');
 * var affix = require('module/Affix');
 *
 * //initialize affix
 * init({
 *
 *     //specify (optional) size functions (choose names you want)
 *     headerTop: function () { reutrn 123; },
 *     fixedValue50: 50
 * });
 *
 * //define needed size functions
 * addSizeFunction('rightNavTop', function () { return 300; });
 * addSizeFunction('rightNavBottom', 400);
 *
 * //setup elements using size funcitons defined in the previous step
 * setup(jQuery(selectorHeader), { topOff: 'headerTop' });
 * setup(jQuery(selectorRightNav), {
 *     topOff:      'rightNavTop',    //maximum top scroll offset (after this class will be changed)
 *     bottomOff:   'rightNavBottom', //maximum bottom offset (element will move to this at most)
 *     fixedTopOff: 'fixedValue50'    //fixed top offset on the element when scrolling
 * });
 *
 * refresh(); //call this in case there is a change in elements that are used to
 *                  //calculate offsets. Recalculation is made only on
 *                  //1. page resize
 *                  //2. manual refresh() call
 *
 * @module app/components/Affix
 */
import _ from 'lodash';
import DeviceDetector from './DeviceDetector';
import jQuery from 'jquery';

/**
 * Parse int from val and if NaN then returns default
 *
 * @param   {String} val         value to be parsed
 * @param   {Number} defaultVal  default value
 *
 * @returns {Number}             parsed number
 *
 * @private
 */
var parseIntOrDefault = function parseIntOrDefault(val, defaultVal) {

    return parseInt(val) || defaultVal;
};

/**
 * Add and remove classes from selectorResult based on the condition provided
 *
 * @param   {Object}   element         single element
 * @param   {Function} condition       funciton that returnes true or false
 * @param   {String}   classToAdd      classes to add, separated with space
 * @param   {String}   classToRemove1  classes to remove, separated with space
 * @param   {String}   classToRemove2  classes to remove, separated with space
 *
 * @returns {void}
 *
 * @private
 */
var addRemoveClass = function addRemoveClass(element, condition, classToAdd, classToRemove1, classToRemove2) {

    if (condition) {

        var classList = element.classList;

        classList.remove(classToRemove1);
        classList.remove(classToRemove2);
        classList.add(classToAdd);
    }
};

/**
 * Remove element from the list/array if condition is true
 *
 * @param   {Array}   list       src array
 * @param   {Number}  index      index
 * @param   {Boolean} condition  condition
 *
 * @returns {void}
 *
 * @private
 */
var removeElementFromList = function removeElementFromList(list, index, condition) {

    if (condition) {

        list.splice(index, 1);
    }
};

/**
 * Remove elements (detached from DOM) from the list
 *
 * This function checks if elements (list[i].element) exist in DOM by
 * getting closest 'document' element to each of them.
 *
 * @param   {Array} list src list/array ([{element: jQueryElement, ...}, {element: jQueryElement, ...}])
 *
 * @returns {void}
 *
 * @private
 */
var removeOldElements = function removeOldElements(list) {

    for (var i = list.length - 1; i >= 0; --i) {

        removeElementFromList(list, i,
            list[i].element.closest(doc.documentElement).length === 0);
    }
};

/**
 * Normalize funciton which means that if value provided the new
 * function will be created dynamically. This newly created function
 * will return value provided.
 *
 * @param   {Function|Number}   funcOrValue function or value
 *
 * @returns {Function}          original function or new that returns value
 *
 * @private
 */
var normalizeFunction = function normalizeFunction(funcOrValue) {

    var directVal = parseInt(funcOrValue);
    var ret = funcOrValue;

    if (!isNaN(directVal)) {

        ret = function dynamicNormalizedfunction() {
            return directVal;
        };
    }
    return ret;
};

/**
 * List of elements used to recalc
 *
 * @example
 * [{
            *     element: jQuerySelectorResult,
            *     action: Function,
            *     data: {
                *         isTop: Boolean, isBottom: Boolean, isNormal: Boolean, origTop: Number,
                *         cssTop: Number,
                *         height: Number
                *     }
                * },{...}]
 *
 * @type {Object}
 *
 * @private
 */
var watchElements = [];

/**
 * Object to keep calculated affix sizes
 *
 * @example
 * {offset_1: 123, offset_2: 321, ...}
 *
 * @type {Object}
 *
 * @private
 */
var affixSizes = {};

/**
 * Object to keep affix size functions
 *
 * @example
 * {offset_1: function () { return 123; }, offset_2: function () { return 321; }, ...}
 *
 * @type {Object}
 *
 * @private
 */
var affixFunctions = {
    'spyFixed0': function spyFixed0() {
        return 0;
    },
};

/**
 * Default affix options
 *
 * @type {Object}
 *
 * @private
 */
var defaultInitOptions = {
    topOff: 'spyFixed0',
    bottomOff: 'spyFixed0',
    fixedTopOff: 'spyFixed0',
    defaultTopOff: 'spyFixed0',
};

/**
 * Window object
 *
 * @type {Object}
 *
 * @private
 */
var win = window;

/**
 * Document object
 *
 * @type {Object}
 *
 * @private
 */
var doc = document;

/**
 * Global options
 *
 * @type {Object}
 *
 * @private
 */
var options = {
    scrollHeight: 0, //height of the document (that can be scrolled)
    targetScrollTop: 0, //current top scroll value
    scrollAttached: false, //indicates if scroll event was bound
    resizeAttached: false, //indicates if rasize/orientation change event was bound
    visChangeAttached: false, //indicates if visibility event was bound
    hiddenPropertyName: 'hidden', //window hidden property name
    visibilityChangeEventName: 'visibilitychange', //visibility change event name
};

/**
 * Timer that checks if document size changed (eg. via ajax call)
 *
 * @type {Object}
 */
var fastCheckTimer = null;

/**
 * Update size function used in each call in @see updateAffixSizes
 *
 * @param   {String}   name   size function name
 * @param   {Function} action size function action
 *
 * @returns {void}
 *
 * @private
 */
var affixSizeUpdateEach = function affixSizeUpdateEach(name, action) {

    affixSizes[name] = action();
};

/**
 * Helper function to recalculate elements height
 *
 * @param   {Number} ind index on the list
 * @param   {Object} obj element object {element: ...}
 *
 * @returns {void}
 *
 * @private
 */
var recalcSizesEach = function recalcSizesEach(ind, obj) {

    var bounding = obj.elementRaw.getBoundingClientRect();

    obj.data.height = bounding.bottom - bounding.top;
};

/**
 * Recalculate affixed elements sizes (size functions and element height).
 * This loops through all registered affix size functions and through all
 * elements and updates sizes.
 *
 * @returns {void}
 *
 * @private
 */
var updateAffixSizes = function updateAffixSizes() {

    if (!DeviceDetector.isMobile()) {

        options.scrollHeight =
            doc.documentElement.getBoundingClientRect().height;

        jQuery.each(affixFunctions, affixSizeUpdateEach);
        jQuery.each(watchElements, recalcSizesEach);

        scrollHandler();
    }
};

/**
 * Set top offset in normal action handler
 *
 * @param   {Object} opts options
 *
 * @returns {string} affix option
 *
 * @private
 */
var setActionNormalTopOffset = function setActionNormalTopOffset(opts) {

    if (affixSizes[opts.options.fixedTopOff] === 0) {

        opts.self.style.top = '';
    } else {

        opts.self.style.top = affixSizes[opts.options.fixedTopOff] + 'px';
    }
};

/**
 * Array of actions to be called on each element. Contains conditions
 * and actions to me performed when condition is met.
 *
 * @type {Array}
 *
 * @private
 */
var handlerActions = [
    {
        /**
         * Check if topOff is set, if yes then check scroll offset position.
         * This first condition is met if scroll position is less than topOff.
         *
         * @param   {Object}  opts calculated variables
         *
         * @returns {Boolean}      true if actions should take place
         *
         * @private
         */
        cond: function condTop(opts) {

            return (affixSizes.hasOwnProperty(opts.options.topOff)) &&
                (opts.targetScrollTop < affixSizes[opts.options.topOff]);
        },
        /**
         * Action to be taken if top condition is met
         *
         * @param   {Object} opts calculated variables
         *
         * @returns {Boolean} true
         *
         * @private
         */
        action: function actionTop(opts) {

            if (!opts.data.isTop) {

                addRemoveClass(opts.self, !opts.data.isTop,
                    'affix-top', //add
                    'affix', 'affix-bottom'); //remove

                opts.data.isBottom = false;
                opts.data.isNormal = false;
                opts.data.isTop = true;
            }
            return true;
        },
    },
    {
        /**
         * Check if element (with it's top offset) fits into space between offset top
         * and either the end of the document or bottomOff size
         *
         * @param   {Object} opts calculated variables
         *
         * @returns {Boolean}     true if actions should take place
         *
         * @private
         */
        cond: function condNormal(opts) {

            return opts.scrollHeight -
                affixSizes[opts.options.bottomOff] -
                opts.targetScrollTop >=
                opts.data.height +
                (affixSizes[opts.options.fixedTopOff] || opts.data.cssTop);
        },
        /**
         * Action to be taken if normal condition is met
         *
         * @param   {Object} opts calculated variables
         *
         * @returns {Boolean} true
         *
         * @private
         */
        action: function actionNormal(opts) {

            if (!opts.data.isNormal) {

                addRemoveClass(opts.self, !opts.data.isNormal,
                    'affix', //add
                    'affix-top', 'affix-bottom'); //remove

                opts.data.isTop = opts.data.isBottom = false;
                opts.data.isNormal = true;

                setActionNormalTopOffset(opts);
            }

            return true;
        },
    },
    {
        /**
         * Fix to bottom condition. Always true as it's the last one
         *
         * @returns {Boolean} true if actions should take place
         *
         * @private
         */
        cond: function condBottom() {

            return true;
        },
        /**
         * Action taken when bottom condition is met
         *
         * @param   {Object}  opts calculated variables
         *
         * @returns {Boolean} true
         *
         * @private
         */
        action: function actionBottom(opts) {

            addRemoveClass(opts.self, !opts.data.isBottom,
                'affix-bottom', //add
                'affix', 'affix-top'); //remove

            opts.data.isTop = opts.data.isNormal = false;
            opts.data.isBottom = true;

            opts.self.style.top = (opts.scrollHeight - opts.targetScrollTop -
                affixSizes[opts.options.bottomOff] - opts.data.height) + 'px';

            return true;
        },
    },
];

/**
 * Handle single element change when scrolling. This function is called
 * everytime 'scroll' event is triggered and calls actions from handlerActions array.
 *
 * @param  {Object} optionsLocal       element behaviour options
 * @param  {Number} targetScrollTop    target scroll top value
 *
 * @returns {void}
 *
 * @private
 */
var handleSingleElement = function handleSingleElement(optionsLocal, targetScrollTop) {

    var opts = {
        data: this.data,
        self: this.elementRaw,
        options: optionsLocal,
        targetScrollTop: targetScrollTop,
        scrollHeight: options.scrollHeight,
    };

    var res1 = handlerActions[0].cond(opts) &&
        handlerActions[0].action(opts);

    var res2 = !res1 && handlerActions[1].cond(opts) && // eslint-disable-line no-unused-vars
        handlerActions[1].action(opts);

    res2 = !res1 && !res2 && handlerActions[2].cond(opts) &&
        handlerActions[2].action(opts);
};

/**
 * Initialize affix of element (by jquery selector)
 *
 * @param   {Object} jQuerySelectorResult jQuery selector result
 * @param   {Object} optionsLocal         Initialization options
 *
 * @returns {void}
 *
 * @private
 */
var initElements = function initElements(jQuerySelectorResult, optionsLocal) {

    jQuerySelectorResult.each(function initElementsEach(index, value) {

        var data = {
            element: jQuery(value),
            elementRaw: value,
            action: undefined,
            data: {
                isTop: false, isBottom: false, isNormal: false, origTop: 0,
                cssTop: parseIntOrDefault(optionsLocal.defaultTopOff, 0),
                height: 0,
            },
        };

        data.action = handleSingleElement.bind(data, optionsLocal);

        watchElements.push(data);
    });

    removeOldElements(watchElements);
};

/**
 * Function called when scroll event is triggered.
 *
 * @param   {Object} options scrolling options
 *
 * @returns {void}
 *
 * @private
 */
var scrollHandler = function scrollHandler() {

    options.targetScrollTop = win.pageYOffset;

    for (var i = watchElements.length - 1; i >= 0; i--) {

        watchElements[i].action(options.targetScrollTop);
    }
};

/**
 * Bing scrollHandler to scroll event on options.target
 *
 * @returns {void}
 */
var bindScroll = function bindScroll() {

    var scrollAttached = options.scrollAttached;

    if (!scrollAttached) {

        win.addEventListener('scroll', scrollHandler);

        options.scrollAttached = true;
    }
};

/**
 * Initialize scroll. Bind event and simulate first scroll
 *
 * @returns {void}
 */
var initScroll = function initScroll() {

    bindScroll();
    scrollHandler();
};

/**
 * Initialize affix on jQuery selector result elements
 *
 * @param   {Object} jQuerySelectorResult jquery selector result
 * @param   {Object} optionsParam         options for element init
 *
 * @returns {void}
 *
 * @private
 */
var setupAffix = function setupAffix(jQuerySelectorResult, optionsParam) {

    if (!DeviceDetector.isMobile()) {

        var options = jQuery.extend({}, defaultInitOptions, optionsParam);

        initElements(jQuerySelectorResult, options);
        initScroll();
    }
};

/**
 * Register new function that calculates size
 *
 * @param   {String}    name Function name
 * @param   {Function}  func Function returning size
 *
 * @returns {void}
 *
 * @private
 */
var addAffixSizeFunction = function addAffixSizeFunction(name, func) {

    if (func !== undefined) {

        affixFunctions[name] = normalizeFunction(func);
    }
};

/**
 * Binds function to window resize and orientationchange event.
 * Uses _.debounce function that decreases number of handler calls.
 *
 * @returns {void}
 *
 * @private
 */
var initWindowResize = function initWindowResize() {

    if (options.resizeAttached === false) {

        win.addEventListener('resize', _.debounce(updateAffixSizes, 100));
        win.addEventListener('orientationchange', updateAffixSizes);

        options.resizeAttached = true;
    }
};

/**
 * Handler for visibility change. Stops checkig page size changes when
 * current page (window/tab) is not active.
 *
 * @returns {void}
 *
 * @private
 */
var handlePageVisibilityChange = function handlePageVisibilityChange() {

    if (options.visChangeAttached &&
        !doc[options.hiddenPropertyName]) {

        startFastCheckTimer();
    }
    else {

        stopFastCheckTimer();
    }
};

/**
 * Checks and saves 'hidden' property name used to check if page is
 * visible.
 *
 * @returns {void}
 *
 * @private
 */
var getHiddenPropName = function getHiddenPropName() {

    //fallback to prefixed names in old webkit browsers
    if (doc.webkitHidden !== undefined) {

        options.hiddenPropertyName = 'webkitHidden';
        options.visibilityChangeEventName = 'webkitvisibilitychange';
    }
};

/**
 * Binds visibility change event.
 *
 * @returns {void}
 *
 * @private
 */
var intitWindowVisibilityChange = function intitWindowVisibilityChange() {

    if (options.visChangeAttached === false) {

        getHiddenPropName();

        doc.addEventListener(options.visibilityChangeEventName,
            handlePageVisibilityChange);

        options.visChangeAttached = true;
    }
};

/**
 * Fast check if page size has changed (due to for eg. ajax call)
 *
 * @returns {Boolean} false if page has changed, true otherwise
 *
 * @private
 */
var fastCheck = function fastCheck() {

    return options.scrollHeight ===
        doc.documentElement.getBoundingClientRect().height;
};

/**
 * Starts fast page size check timer
 *
 * @returns {void}
 *
 * @private
 */
var startFastCheckTimer = function startFastCheckTimer() {

    fastCheckTimer = setInterval(function startFastCheckTimerHandler() {

        if (!fastCheck()) {

            updateAffixSizes();
        }
    }, 500);
};

/**
 * Stops fat page size check timer
 *
 * @returns {void}
 *
 * @private
 */
var stopFastCheckTimer = function stopFastCheckTimer() {

    clearInterval(fastCheckTimer);
};

/**
 * Flat object to contain public methods
 *
 * @type {Object}
 *
 * @public
 */
var api = {

    /**
     * Setup affix on element
     *
     * @type {Object}
     *
     * @returns {void}
     *
     * @public
     */
    setup: setupAffix,

    /**
     * Register new function that calculates size
     *
     * @param   {String}    name Function name
     * @param   {Function}  func Function returning size
     *
     * @returns {Object}    Affix object for chaining purposes
     *
     * @public
     */
    addSizeFunction: function addSizeFunction(name, func) {

        if (!DeviceDetector.isMobile()) {

            addAffixSizeFunction(name, func);
        }

        return api;
    },

    /**
     * Recalculate affixed elements sizes (size functions and element height).
     * This loops through all registered affix size functions and through all
     * elements and updates sizes.
     *
     * @returns {void}
     *
     * @public
     */
    refresh: updateAffixSizes,

    /**
     * Initialize affix module
     *
     * @param   {Object} sizeFunctions size function object
     *
     * @returns {void}
     *
     * @public
     */
    init: function init(sizeFunctions) {

        if (!DeviceDetector.isMobile()) {

            jQuery.each(sizeFunctions, addAffixSizeFunction);

            initWindowResize();
            intitWindowVisibilityChange();

            startFastCheckTimer();
        }
    },

};

export default api;
